import { RuntimeCatalog } from "./schema"
import { assertRuntimeContentRelease, ContentReleaseManifest } from "./release"

export type ReadonlyContent<T> = T extends object ? { readonly [K in keyof T]: ReadonlyContent<T[K]> } : T
export interface ContentSnapshot {
    readonly generation: number
    readonly manifest: ReadonlyContent<ContentReleaseManifest>
    readonly catalog: ReadonlyContent<RuntimeCatalog>
}
/** Identity is store-owned; copying this handle does not authorize activation. */
export interface PreparedContent {
    readonly revision: string
    readonly baseGeneration: number
}

const freeze = <T>(value: T): ReadonlyContent<T> => {
    if (value && typeof value === "object") {
        Object.values(value).forEach(freeze)
        Object.freeze(value)
    }
    return value as ReadonlyContent<T>
}

const snapshot = (manifest: unknown, catalog: unknown, generation: number): ContentSnapshot => {
    // A detached JSON snapshot prevents a caller mutating active or pending content afterward.
    const copy = JSON.parse(JSON.stringify({ manifest, catalog }))
    assertRuntimeContentRelease(copy.manifest, copy.catalog)
    return freeze({ generation, manifest: copy.manifest as ContentReleaseManifest, catalog: copy.catalog })
}

/**
 * Browser-safe release staging primitive. Preparation cannot change active content; activation
 * is one synchronous pointer swap. Runtime adapters and the network coordinator must prepare
 * their dependent state before using this store to activate gameplay.
 */
export class ContentStore {
    private current: ContentSnapshot
    private pending?: { handle: PreparedContent; snapshot: ContentSnapshot }
    private preparing = false

    constructor(manifest: unknown, catalog: unknown) {
        this.current = snapshot(manifest, catalog, 0)
    }

    get active(): ContentSnapshot { return this.current }

    private assertIdle() {
        if (this.preparing) throw new Error("Content preparation is already in progress")
    }

    prepare(manifest: unknown, catalog: unknown, prepareDependents?: (candidate: ContentSnapshot) => void): PreparedContent {
        this.assertIdle()
        const next = snapshot(manifest, catalog, this.current.generation + 1)
        // A dependent preparation failure must not replace an earlier valid pending release.
        this.preparing = true
        try { prepareDependents?.(next) } finally { this.preparing = false }
        const handle = Object.freeze({ revision: next.catalog.revision, baseGeneration: this.current.generation })
        // Only successful preparation supersedes an earlier candidate.
        this.pending = { handle, snapshot: next }
        return handle
    }

    activate(handle: PreparedContent): ContentSnapshot {
        this.assertIdle()
        if (!this.pending || this.pending.handle !== handle || handle.baseGeneration !== this.current.generation)
            throw new Error("Content preparation is stale or belongs to another store")
        this.current = this.pending.snapshot
        this.pending = undefined
        return this.current
    }

    discard(handle: PreparedContent): void {
        this.assertIdle()
        if (this.pending?.handle === handle) this.pending = undefined
    }
}
